import {
  Users,
  UserPlus,
  MoreHorizontal,
  ShieldCheck,
  ShieldAlert,
  Eye,
  Info,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

type RoleKey = 'Administrator' | 'Mitglied' | 'Betrachter';
type StatusKey = 'Aktiv' | 'Eingeladen';

interface DemoUser {
  name: string;
  email: string;
  initials: string;
  role: RoleKey;
  status: StatusKey;
  lastLoginKey: string;
}

const ROLE_STYLES: Record<RoleKey, { bg: string; fg: string; border: string }> = {
  Administrator: { bg: 'rgba(168,85,247,0.14)', fg: '#C4A2FF', border: 'rgba(168,85,247,0.32)' },
  Mitglied: { bg: 'rgba(45,126,255,0.14)', fg: '#7FB0FF', border: 'rgba(45,126,255,0.32)' },
  Betrachter: { bg: 'rgba(148,163,194,0.12)', fg: '#94A3C2', border: 'rgba(148,163,194,0.26)' },
};

const ROLE_ICON: Record<RoleKey, typeof ShieldCheck> = {
  Administrator: ShieldAlert,
  Mitglied: ShieldCheck,
  Betrachter: Eye,
};

// Maps RoleKey -> translation key suffix under users.roles.*
const ROLE_I18N_KEY: Record<RoleKey, string> = {
  Administrator: 'administrator',
  Mitglied: 'member',
  Betrachter: 'viewer',
};

// Maps StatusKey -> translation key suffix under users.status.*
const STATUS_I18N_KEY: Record<StatusKey, string> = {
  Aktiv: 'active',
  Eingeladen: 'invited',
};

const C = {
  surface: 'var(--bnkp-surface, #141B27)',
  elevated: 'var(--bnkp-elevated, #1A2334)',
  secondary: 'var(--bnkp-recessed, #0A0F1C)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  textPrimary: 'var(--bnkp-text, #E2E8F0)',
  textSecondary: 'var(--bnkp-text2, #94A3C2)',
  textTertiary: 'var(--bnkp-text3, #4A5568)',
  primary: 'var(--bnkp-primary, #2D7EFF)',
  green: 'var(--bnkp-green, #34D399)',
};

export function UsersScreen({ className }: { className?: string }) {
  const { t } = useTranslation('demo');

  const USERS: DemoUser[] = [
    { name: 'Jan Pfrommer', email: 'jan.pfrommer@securesight.ai', initials: 'JP', role: 'Administrator', status: 'Aktiv', lastLoginKey: 'minutesAgo' },
    { name: 'Kai Bennett', email: 'kai.bennett@securesight.ai', initials: 'KB', role: 'Administrator', status: 'Aktiv', lastLoginKey: 'today' },
    { name: 'Lisa Weber', email: 'lisa.weber@securesight.ai', initials: 'LW', role: 'Mitglied', status: 'Aktiv', lastLoginKey: 'yesterday' },
    { name: 'Tom Schäfer', email: 'tom.schaefer@securesight.ai', initials: 'TS', role: 'Mitglied', status: 'Aktiv', lastLoginKey: 'june10' },
    { name: 'M. Yilmaz', email: 'm.yilmaz@securesight.ai', initials: 'MY', role: 'Betrachter', status: 'Aktiv', lastLoginKey: 'june06' },
    { name: t('users.invitedGuestName'), email: 'anna.koch@musterkunde.de', initials: 'AK', role: 'Mitglied', status: 'Eingeladen', lastLoginKey: 'pending' },
  ];

  const STATS = [
    { key: 'total', label: t('users.stats.total'), value: '6' },
    { key: 'active', label: t('users.stats.active'), value: '5' },
    { key: 'roles', label: t('users.stats.roles'), value: '3' },
  ];

  return (
    <div className={className} style={{ fontFamily: 'Inter, sans-serif', color: C.textPrimary, maxWidth: 900 }}>
      <style>{`@keyframes ssUsersPulse{0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(52,211,153,0.45)}50%{opacity:.65;box-shadow:0 0 0 4px rgba(52,211,153,0)}}`}</style>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 10, background: 'rgba(45,126,255,0.10)', border: '1px solid rgba(45,126,255,0.22)' }}>
            <Users size={20} color={C.primary} aria-hidden />
          </span>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em' }}>{t('users.heading')}</h1>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: C.textSecondary }}>{t('users.subtitle')}</p>
          </div>
        </div>
        <button
          type="button"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: 'none', background: C.primary, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(45,126,255,0.35)' }}
        >
          <UserPlus size={16} aria-hidden /> {t('users.inviteButton')}
        </button>
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        {STATS.map((s) => (
          <div key={s.key} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: '14px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 12, color: C.textSecondary, fontWeight: 500 }}>{s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 2, letterSpacing: '-0.02em' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
        <div role="table" aria-label={t('users.tableLabel')} style={{ width: '100%' }}>
          <div role="row" style={{ display: 'grid', gridTemplateColumns: '2.4fr 1.1fr 1fr 1.1fr 40px', alignItems: 'center', gap: 12, padding: '11px 18px', background: C.secondary, borderBottom: `1px solid ${C.border}`, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: C.textTertiary }}>
            <span role="columnheader">{t('users.columns.user')}</span>
            <span role="columnheader">{t('users.columns.role')}</span>
            <span role="columnheader">{t('users.columns.status')}</span>
            <span role="columnheader">{t('users.columns.lastLogin')}</span>
            <span role="columnheader" aria-label={t('users.columns.actions')} />
          </div>

          {USERS.map((u, i) => {
            const role = ROLE_STYLES[u.role];
            const RoleIcon = ROLE_ICON[u.role];
            const active = u.status === 'Aktiv';
            return (
              <div
                key={u.email}
                role="row"
                style={{ display: 'grid', gridTemplateColumns: '2.4fr 1.1fr 1fr 1.1fr 40px', alignItems: 'center', gap: 12, padding: '13px 18px', borderBottom: i < USERS.length - 1 ? `1px solid ${C.border}` : 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <span aria-hidden style={{ flex: '0 0 auto', display: 'grid', placeItems: 'center', width: 36, height: 36, borderRadius: '50%', background: active ? 'linear-gradient(135deg,#2D7EFF,#00D4FF)' : C.elevated, border: active ? 'none' : `1px solid ${C.borderStrong}`, color: active ? '#fff' : C.textSecondary, fontSize: 13, fontWeight: 600 }}>
                    {u.initials}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: C.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                  </span>
                </div>

                <span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: role.bg, color: role.fg, border: `1px solid ${role.border}` }}>
                    <RoleIcon size={12} aria-hidden /> {t(`users.roles.${ROLE_I18N_KEY[u.role]}`)}
                  </span>
                </span>

                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: active ? C.textPrimary : C.textSecondary }}>
                  <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: active ? C.green : C.textTertiary, animation: active ? 'ssUsersPulse 2.4s ease-in-out infinite' : 'none' }} />
                  {t(`users.status.${STATUS_I18N_KEY[u.status]}`)}
                </span>

                <span style={{ fontSize: 13, color: C.textSecondary }}>{t(`users.lastLogin.${u.lastLoginKey}`)}</span>

                <button type="button" aria-label={t('users.rowActions', { name: u.name })} style={{ display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, cursor: 'pointer' }}>
                  <MoreHorizontal size={16} aria-hidden />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Caption */}
      <p style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '14px 2px 0', fontSize: 12.5, color: C.textTertiary }}>
        <Info size={13} aria-hidden />
        {t('users.caption')}
      </p>
    </div>
  );
}
